import { OrganizationRoles, ProjectRoles } from "./Enums";

/**
 * Content for the role-permissions panel (AKL-793).
 *
 * The canonical definitions live in the API, in
 * `src/authorization/organization_authorization.py` and
 * `project_authorization.py`. This table is deliberately **curated from** those
 * definitions rather than generated from them, and it must stay that way until
 * they are cleaned up: an audit on 2026-09-11 traced all 45 permission members
 * to the code that checks them and found 21 that are checked nowhere. Eight of
 * those describe features with no screen in Akili — subprojects were never
 * built, and initiatives were removed from the UI by AKL-309. Three more
 * (VIEW_ALL_PROJECTS_IN_ORG, DOWNLOAD_DATA_FROM_ALL_PROJECTS_IN_ORG,
 * VIEW_OWN_PROJECT_IN_ORG) read exactly like the answer this panel is asked
 * for, which is why rendering the mapping verbatim would be worse than
 * rendering nothing.
 *
 * Each row below records the permission members it summarises, so a change on
 * the API side is greppable from here. When the dead members are removed or
 * implemented, this file is the one to revisit.
 */

/** Which set of roles a surface is assigning — organisations have four, projects five. */
export type RoleScope = "organization" | "project";

export type RoleKey = "OWNER" | "EDITOR" | "CONTRIBUTOR" | "TAGGER" | "VIEWER";

export type RoleColumn = {
  key: RoleKey;
  /** The role id the API uses, taken from Enums so the numbers are not restated. */
  value: number;
  /** Translation key for the title-case display name. */
  labelKey: string;
};

export type PermissionRow = {
  /** Translation key for the action, written as something a person does. */
  labelKey: string;
  granted: RoleKey[];
};

export type RoleTable = {
  titleKey: string;
  /** Heading above the action column. */
  actionHeadingKey: string;
  roles: RoleColumn[];
  rows: PermissionRow[];
};

const ORGANIZATION_ROLES: RoleColumn[] = [
  { key: "OWNER", value: OrganizationRoles.OWNER, labelKey: "Owner" },
  { key: "EDITOR", value: OrganizationRoles.EDITOR, labelKey: "Editor" },
  {
    key: "CONTRIBUTOR",
    value: OrganizationRoles.CONTRIBUTOR,
    labelKey: "Contributor",
  },
  { key: "VIEWER", value: OrganizationRoles.VIEWER, labelKey: "Viewer" },
];

const PROJECT_ROLES: RoleColumn[] = [
  { key: "OWNER", value: ProjectRoles.OWNER, labelKey: "Owner" },
  { key: "EDITOR", value: ProjectRoles.EDITOR, labelKey: "Editor" },
  {
    key: "CONTRIBUTOR",
    value: ProjectRoles.CONTRIBUTOR,
    labelKey: "Contributor",
  },
  { key: "TAGGER", value: ProjectRoles.TAGGER, labelKey: "Tagger" },
  { key: "VIEWER", value: ProjectRoles.VIEWER, labelKey: "Viewer" },
];

const ORGANIZATION_TABLE: RoleTable = {
  titleKey: "What each organization role can do",
  actionHeadingKey: "In this organization",
  roles: ORGANIZATION_ROLES,
  rows: [
    // VIEW_ORG_DETAILS
    {
      labelKey: "See the organization and its details",
      granted: ["OWNER", "EDITOR", "CONTRIBUTOR", "VIEWER"],
    },
    // EDIT_ORG_DETAILS
    {
      labelKey: "Edit organization details",
      granted: ["OWNER", "EDITOR"],
    },
    // VIEW_SENSORS_IN_ORG
    {
      labelKey: "See cameras and other devices",
      granted: ["OWNER", "EDITOR", "CONTRIBUTOR", "VIEWER"],
    },
    // CREATE_SENSORS_IN_ORG + EDIT_SENSORS_IN_ORG — the same three roles hold
    // both, and EDIT also authorises deletion, so they are one row.
    {
      labelKey: "Add and edit cameras and other devices",
      granted: ["OWNER", "EDITOR", "CONTRIBUTOR"],
    },
    // CREATE_PROJECT_IN_ORG
    {
      labelKey: "Create projects",
      granted: ["OWNER", "EDITOR", "CONTRIBUTOR"],
    },
    // VIEW_USER_DETAILS_IN_ORG — gates the organization Users tab in the web
    // app; the API does not check it.
    {
      labelKey: "See the members list",
      granted: ["OWNER", "EDITOR"],
    },
    // INVITE_USER_AS_VIEWER
    {
      labelKey: "Invite and remove Viewers",
      granted: ["OWNER", "EDITOR"],
    },
    // INVITE_USER_AS_EDITOR_CONTRIBUTOR + INVITE_USER_AS_OWNER — both Owner
    // only, so they are one row.
    {
      labelKey: "Invite and remove Owners, Editors and Contributors",
      granted: ["OWNER"],
    },
  ],
};

const PROJECT_TABLE: RoleTable = {
  titleKey: "What each project role can do",
  actionHeadingKey: "In this project",
  roles: PROJECT_ROLES,
  rows: [
    // VIEW_PROJECT_DETAILS + VIEW_DEPLOYMENTS_LOCATIONS
    {
      labelKey: "See the project, its locations and deployments",
      granted: ["OWNER", "EDITOR", "CONTRIBUTOR", "TAGGER", "VIEWER"],
    },
    // VIEW_IMAGES
    {
      labelKey: "View photos",
      granted: ["OWNER", "EDITOR", "CONTRIBUTOR", "TAGGER", "VIEWER"],
    },
    // EDIT_PROJECT_DETAILS
    {
      labelKey: "Edit project details",
      granted: ["OWNER", "EDITOR"],
    },
    // CREATE_DEPLOYMENTS_LOCATIONS + EDIT_DEPLOYMENTS_LOCATIONS — EDIT also
    // authorises deletion.
    {
      labelKey: "Add and edit locations and deployments",
      granted: ["OWNER", "EDITOR", "CONTRIBUTOR"],
    },
    // UPLOAD_IMAGES
    {
      labelKey: "Upload photos",
      granted: ["OWNER", "EDITOR", "CONTRIBUTOR"],
    },
    // EDIT_IDENTIFICATIONS
    {
      labelKey: "Identify animals in photos",
      granted: ["OWNER", "EDITOR", "CONTRIBUTOR", "TAGGER"],
    },
    // DOWNLOAD_DATA — Viewer holds this and Contributor and Tagger do not.
    // That is what the definitions say; it is not a transcription slip.
    {
      labelKey: "Download data",
      granted: ["OWNER", "EDITOR", "VIEWER"],
    },
    // VIEW_HUMAN_IMAGES_IN_CATALOGUED_IF_DELETE_NOT_SELECTED — enforced in the
    // export path only. Every project role can already see human images on
    // screen, because the review feed checks VIEW_IMAGES. The row says
    // "download" deliberately; changing it to "see" would claim a safeguard
    // the product does not have.
    {
      labelKey: "Download photos containing people",
      granted: ["OWNER"],
    },
    // MANAGE_AI_MAPPINGS
    {
      labelKey: "Manage AI model mappings",
      granted: ["OWNER"],
    },
    // VIEW_USER_DETAILS_IN_PROJECT — gates the project Users tab in the web
    // app; the API does not check it.
    {
      labelKey: "See the members list",
      granted: ["OWNER", "EDITOR"],
    },
    // INVITE_VIEWERS_CONTRIBUTORS_TAGGERS
    {
      labelKey: "Invite and remove Viewers, Contributors and Taggers",
      granted: ["OWNER", "EDITOR"],
    },
    // INVITE_OWNERS_EDITORS
    {
      labelKey: "Invite and remove Owners and Editors",
      granted: ["OWNER"],
    },
  ],
};

/**
 * How an organization role reaches the organization's projects.
 *
 * Source: the "Permissions Inheritance Overview" design doc, confirmed against
 * `project_service.py:777-892`. Inheritance is additive by design — AKL-687 was
 * closed Works As Designed when QA filed the persistence of inherited roles as
 * a defect — which is why the last sentence is here rather than omitted.
 */
export const inheritanceNote = {
  leadKey:
    "Organization roles and project roles are separate. A role in an organization does not by itself give access to the organization's projects.",
  // Worded to match the project form's own label, "Inherit User Roles from
  // Organization", without quoting it — the label is a checkbox caption and
  // does not read as a clause.
  conditionKey:
    "When a project is set to inherit user roles from its organization, people are given a project role as they join the organization, or when their organization role is changed:",
  /**
   * The sentence each mapping is rendered into. It lives here rather than as a
   * literal in the component so the translation-coverage test picks it up from
   * the data, the way it picks up every other key.
   */
  mappingTemplateKey: "Organization {{from}} becomes Project {{to}}",
  mappings: [
    { fromKey: "Owner", toKey: "Editor" },
    { fromKey: "Editor", toKey: "Viewer" },
  ],
  noInheritanceKey:
    "Organization Contributors and Viewers do not get a project role.",
  persistenceKey:
    "Anyone who already has a role on the project keeps it. A project role given this way stays in place even if the organization role is later changed or removed.",
};

/** The table for a scope. */
export const getRoleTable = (scope: RoleScope): RoleTable =>
  scope === "organization" ? ORGANIZATION_TABLE : PROJECT_TABLE;
